import { useEffect, useState } from 'react';
import { v5 } from 'uuid';

const appKey = process.env.KAKAO_APP_KEY;

const kakaoMapScriptElementId = v5(
  'https://certi.programmers.co.kr/kakao-map-script',
  v5.URL,
);

const kakaoScriptElementId = v5(
  'https://certi.programmers.co.kr/kakao-script',
  v5.URL,
);

const loadWaitInterval = 10;
const maxRetryCount = 10;

export const getKakao = async () => {
  if (!document.getElementById(kakaoScriptElementId) || !window.Kakao) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://developers.kakao.com/sdk/js/kakao.js';
      script.id = kakaoScriptElementId;
      script.onload = () => {
        let retry = 0;
        const interval = () => {
          if (!window.Kakao) {
            if (retry > maxRetryCount) {
              script.remove();
              reject(new ReferenceError('missing required Kakao class'));
            } else {
              retry += 1;
              setTimeout(interval, loadWaitInterval);
            }
          } else {
            resolve();
          }
        };
        interval();
      };
      document.body.appendChild(script);
    });
  }

  const Kakao = window.Kakao;

  if (!Kakao) {
    throw new ReferenceError('missing required Kakao class');
  }

  if (!appKey) {
    throw new ReferenceError('missing required Kakao app key');
  }

  !Kakao.isInitialized() && Kakao.init(appKey);
  return Kakao;
};

export const useKakao = () => {
  const [kakao, setKakao] = useState<typeof window['Kakao'] | null>(null);

  useEffect(() => {
    getKakao().then((Kakao) => {
      setKakao(Kakao);
    }).catch((error) => {
      throw error;
    });
  }, []);

  return kakao;
};

export const getKakaoMap = async () => {
  if (!document.getElementById(kakaoMapScriptElementId) || !window.kakao) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&autoload=false&libraries=services`;
      script.id = kakaoMapScriptElementId;
      script.onload = () => {
        let retry = 0;
        const interval = () => {
          if (!window.kakao) {
            if (retry > maxRetryCount) {
              script.remove();
              reject(new ReferenceError('missing required kakao class'));
            } else {
              retry += 1;
              setTimeout(interval, loadWaitInterval);
            }
          } else {
            resolve();
          }
        };
        interval();
      };
      document.body.appendChild(script);
    });
  }

  const kakao = window.kakao;
  if (!kakao) {
    throw new ReferenceError('missing required kakao class');
  }

  if (!appKey) {
    throw new ReferenceError('missing required kakao app key');
  }

  return kakao;
};

export const useKakaoMap = () => {
  const [kakaoMap, setKakaoMap] = useState<typeof window['kakao'] | null>(null);

  useEffect(() => {
    getKakaoMap().then((kakao) => {
      setKakaoMap(kakao);
    }).catch((error) => {
      throw error;
    });
  }, []);

  return kakaoMap;
};
